<template>
  <gas-page-shell :breadcrumb="breadcrumb">
    <div class="page-head">
      <el-button size="small" icon="el-icon-back" @click="goBack">返回</el-button>
      <el-button type="primary" size="small" icon="el-icon-plus" :disabled="!protocolId" @click="openCreate()">新增参数</el-button>
    </div>

    <div v-if="protocol" class="protocol-tip">
      当前协议：<strong>{{ protocol.protocolName }}</strong>（{{ protocol.protocolCode }}）
    </div>
    <div v-else-if="!protocolLoading" class="protocol-tip warn">请从协议解析管理列表点击「设置」进入，或选择协议</div>

    <div v-if="!protocolId" class="pick-row">
      <el-select v-model="pickProtocolId" filterable placeholder="请选择协议" style="width:320px" @change="onPickProtocol">
        <el-option v-for="p in protocolOptions" :key="p.protocolId" :label="`${p.protocolName} (${p.protocolCode})`" :value="p.protocolId" />
      </el-select>
    </div>

    <div class="filter-row">
      <el-form :inline="true" :model="query" size="small" class="search-form">
        <el-form-item label="关键词检索">
          <el-input v-model="query.keyword" clearable placeholder="请输入" style="width:200px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :disabled="!protocolId" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" :disabled="!protocolId" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-title-row">
      <span class="table-title">协议参数 <em>共 {{ total }} 条记录</em></span>
      <gas-table-toolbar
        :columns="tableColumnDefs"
        :visible-map.sync="tableColumnVisible"
        :selected-count="selectedIds.length"
        :show-batch-delete="tableCanBatchDelete"
        @batch-delete="batchRemove"
      />
    </div>

    <el-table
      :data="list"
      v-loading="loading"
      border
      size="small"
      class="param-table"
      header-cell-class-name="param-table-header"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="44" align="center" />
      <el-table-column label="序号" width="64" align="center">
        <template slot-scope="{ $index }">{{ rowIndex($index) }}</template>
      </el-table-column>
      <el-table-column prop="paramName" label="参数名称" min-width="100" align="center" />
      <el-table-column prop="unit" label="单位" width="88" align="center">
        <template slot-scope="{ row }">{{ row.unit || '—' }}</template>
      </el-table-column>
      <el-table-column label="数据类型" width="110" align="center">
        <template slot-scope="{ row }">{{ dataTypeLabel(row.dataType) }}</template>
      </el-table-column>
      <el-table-column v-if="isMqtt" label="JSON路径" min-width="140" align="center" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.jsonPath || '—' }}</template>
      </el-table-column>
      <el-table-column v-if="isMqtt" label="JSON名称" min-width="110" align="center" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.jsonName || '—' }}</template>
      </el-table-column>
      <el-table-column v-if="!isMqtt" label="起始字节" width="96" align="center">
        <template slot-scope="{ row }">{{ row.startByte != null ? row.startByte : 0 }}</template>
      </el-table-column>
      <el-table-column v-if="!isMqtt" label="字节数量" width="96" align="center">
        <template slot-scope="{ row }">{{ row.byteCount != null ? row.byteCount : 0 }}</template>
      </el-table-column>
          <el-table-column v-if="!isMqtt" label="解析方式" width="96" align="center">
            <template slot-scope="{ row }">{{ parseMethodLabel(row.parseMethod) }}</template>
          </el-table-column>
          <el-table-column label="计算公式" min-width="140" align="center" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.calcFormula || '—' }}</template>
          </el-table-column>
      <el-table-column label="最大值" width="88" align="center">
        <template slot-scope="{ row }">{{ formatNum(row.maxValue) }}</template>
      </el-table-column>
      <el-table-column label="最小值" width="88" align="center">
        <template slot-scope="{ row }">{{ formatNum(row.minValue) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="96" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" class="op-edit" icon="el-icon-edit" @click="openEdit(row)" />
          <el-button type="text" size="small" class="op-del" icon="el-icon-delete" @click="removeParam(row)" />
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      :current-page="query.pageNum"
      :page-size="query.pageSize"
      :page-sizes="[20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="620px" @closed="form = {}">
      <el-form :model="form" label-width="100px" size="small">
        <el-form-item label="参数名称" required>
          <el-select
            v-if="!isMqtt"
            v-model="form.paramName"
            filterable
            allow-create
            default-first-option
            style="width:100%"
            placeholder="请选择标况总量、压力等统计字段，也可自行输入"
            @change="onParamNameChange"
          >
            <el-option
              v-for="item in paramNameOptions"
              :key="item.name"
              :label="item.unit ? `${item.name}（${item.unit}）` : item.name"
              :value="item.name"
            />
          </el-select>
          <el-input
            v-else
            v-model="form.paramName"
            placeholder="自定义显示名称，如 压力、电池电量、采集时间"
          />
        </el-form-item>
        <el-form-item label="单位">
          <el-input v-model="form.unit" placeholder="如 ug/m³" />
        </el-form-item>
        <el-form-item label="数据类型">
          <el-select v-model="form.dataType" style="width:100%">
            <el-option label="数值型" value="numeric" />
            <el-option label="字符型" value="string" />
            <el-option label="Unix时间" value="unix" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="isMqtt" label="JSON路径">
          <el-input v-model="form.jsonPath" placeholder="如 datas、datas/data，根节点留空" />
        </el-form-item>
        <el-form-item v-if="isMqtt" label="JSON名称" required>
          <el-input v-model="form.jsonName" placeholder="如 ts、press、battery" />
        </el-form-item>
        <el-form-item v-if="!isMqtt" label="起始字节">
          <el-input v-model.number="form.startByte" />
        </el-form-item>
        <el-form-item v-if="!isMqtt" label="字节数量">
          <el-input v-model.number="form.byteCount" />
        </el-form-item>
        <el-form-item v-if="!isMqtt" label="解析方式">
          <el-select v-model="form.parseMethod" style="width:100%">
            <el-option label="BCD码" value="BCD" />
            <el-option label="HEX" value="HEX" />
            <el-option label="ASCII" value="ASCII" />
            <el-option label="Float大端" value="FLOAT_BE" />
            <el-option label="UInt32大端" value="UINT32_BE" />
            <el-option label="Int32大端" value="INT32_BE" />
            <el-option label="UInt16大端" value="UINT16_BE" />
            <el-option label="Int16大端" value="INT16_BE" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="!isUnixDataType" label="计算公式">
          <el-input
            v-model="form.calcFormula"
            type="textarea"
            :rows="2"
            placeholder="用 x 表示解析原始值，如 x*0.1、(x+10)/2。留空则直接使用原始值"
          />
        </el-form-item>
        <el-form-item v-else label="时间转换">
          <div class="form-tip">Unix 时间由程序自动转换为日期时间（yyyy-MM-dd HH:mm:ss），支持 10 位秒 / 13 位毫秒，无需填写计算公式。</div>
        </el-form-item>
        <el-form-item v-if="!isUnixDataType" label="最大值">
          <el-input v-model="form.maxValue" />
        </el-form-item>
        <el-form-item v-if="!isUnixDataType" label="最小值">
          <el-input v-model="form.minValue" />
        </el-form-item>
        <el-form-item label="排序号">
          <el-input v-model.number="form.sortNo" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import gasTableEnhance from '@/mixins/gasTableEnhance'
import { gasApi } from '@/api/gas'
import { paramNameSelectOptions, standardParamPreset, unitForParamName } from '@/config/protocolParamNames'

const isMqttProtocol = (protocol) => {
  if (!protocol) return false
  const comm = String(protocol.commType || '').toLowerCase()
  const mode = String(protocol.applyMode || '').toLowerCase()
  return comm === 'mqtt' || mode === 'mqtt'
}

const emptyParam = (protocol) => {
  const mqtt = isMqttProtocol(protocol)
  return {
    dataType: 'numeric',
    paramCategory: 'JSON',
    jsonPath: '',
    jsonName: '',
    startByte: 0,
    byteCount: 0,
    parseMethod: mqtt ? '' : 'BCD',
    calcFormula: '',
    maxValue: 0,
    minValue: 0,
    sortNo: 0
  }
}

export default {
  components: { GasPageShell },
  mixins: [gasTableEnhance],
  data() {
    return {
      idField: 'paramId',
      removeApi: gasApi.protocol.paramRemove,
      batchRemoveApi: gasApi.protocol.paramBatchRemove,
      protocolId: null,
      protocol: null,
      protocolLoading: false,
      protocolOptions: [],
      pickProtocolId: null,
      loading: false,
      list: [],
      total: 0,
      dialogVisible: false,
      dialogTitle: '新增参数',
      form: {},
      query: {
        pageNum: 1,
        pageSize: 50,
        paramCategory: '',
        keyword: ''
      }
    }
  },
  computed: {
    breadcrumb() {
      const base = ['燃气协议管理', '协议设置']
      if (this.protocol && this.protocol.protocolName) {
        return [...base, this.protocol.protocolName]
      }
      return base
    },
    isMqtt() {
      return isMqttProtocol(this.protocol)
    },
    paramNameOptions() {
      return paramNameSelectOptions(this.form && this.form.paramName)
    },
    isUnixDataType() {
      return String((this.form && this.form.dataType) || '').toLowerCase() === 'unix'
    }
  },
  watch: {
    '$route.query.protocolId': {
      immediate: true,
      handler(id) {
        this.applyProtocolId(id)
      }
    }
  },
  created() {
    this.loadProtocolOptions()
  },
  methods: {
    emptyParam,
    applyProtocolId(id) {
      const num = id ? Number(id) : null
      this.protocolId = num && !Number.isNaN(num) ? num : null
      this.pickProtocolId = this.protocolId
      if (this.protocolId) {
        this.loadProtocol()
        this.loadData()
      } else {
        this.protocol = null
        this.list = []
        this.total = 0
      }
    },
    onPickProtocol(id) {
      this.$router.replace({ path: '/protocol/settings', query: id ? { protocolId: id } : {} })
    },
    async loadProtocolOptions() {
      const res = await gasApi.protocol.list({ pageNum: 1, pageSize: 500 })
      this.protocolOptions = (res.data || {}).records || []
    },
    async loadProtocol() {
      if (!this.protocolId) return
      this.protocolLoading = true
      try {
        const res = await gasApi.protocol.detail(this.protocolId)
        this.protocol = res.data || null
      } finally {
        this.protocolLoading = false
      }
    },
    buildQuery() {
      const p = { ...this.query, protocolId: this.protocolId }
      Object.keys(p).forEach(k => {
        if (p[k] === '') p[k] = undefined
      })
      return p
    },
    async loadData() {
      if (!this.protocolId) return
      this.loading = true
      try {
        const res = await gasApi.protocol.paramList(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { pageNum: 1, pageSize: this.query.pageSize, paramCategory: '', keyword: '' }
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    rowIndex(index) {
      const row = this.list[index]
      if (row && row.sortNo != null) return row.sortNo
      return (this.query.pageNum - 1) * this.query.pageSize + index
    },
    dataTypeLabel(v) {
      if (v === 'string') return '字符型'
      if (v === 'unix') return 'Unix时间'
      return '数值型'
    },
    parseMethodLabel(v) {
      if (v === 'FLOAT_BE') return 'Float大端'
      if (v === 'UINT32_BE') return 'UInt32'
      if (v === 'INT32_BE') return 'Int32'
      if (v === 'UINT16_BE') return 'UInt16'
      if (v === 'INT16_BE') return 'Int16'
      if (v === 'HEX') return 'HEX'
      if (v === 'ASCII') return 'ASCII'
      return 'BCD码'
    },
    formatNum(v) {
      if (v === null || v === undefined || v === '') return '0'
      return String(v)
    },
    goBack() {
      this.$router.push('/protocol/list')
    },
    onParamNameChange(name) {
      const preset = standardParamPreset(this.protocol, name)
      if (preset) {
        Object.keys(preset).forEach(key => this.$set(this.form, key, preset[key]))
        return
      }
      const unit = unitForParamName(name)
      if (unit) this.$set(this.form, 'unit', unit)
    },
    openCreate() {
      if (!this.protocolId) return this.$message.warning('请先选择协议')
      this.dialogTitle = '新增参数'
      this.form = { ...emptyParam(this.protocol), protocolId: this.protocolId }
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = '修改参数'
      this.form = { ...row }
      this.dialogVisible = true
    },
    async submitForm() {
      if (!this.form.paramName) return this.$message.warning('请填写参数名称')
      if (this.isMqtt && !(this.form.jsonName || '').trim()) {
        return this.$message.warning('请填写 JSON 名称')
      }
      this.form.protocolId = this.protocolId
      if (this.isMqtt) {
        this.form.paramCategory = 'JSON'
        this.form.parseMethod = ''
        this.form.startByte = 0
        this.form.byteCount = 0
        this.form.jsonPath = (this.form.jsonPath || '').trim()
        this.form.jsonName = (this.form.jsonName || '').trim()
      }
      await gasApi.protocol.paramSave(this.form)
      this.$message.success('保存成功')
      this.dialogVisible = false
      this.loadData()
    },
    async removeParam(row) {
      await this.$confirm('确认删除该参数？', '提示', { type: 'warning' })
      await gasApi.protocol.paramRemove(row.paramId)
      this.$message.success('删除成功')
      this.loadData()
    }
  }
}
</script>

<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.protocol-tip {
  margin-bottom: 16px;
  font-size: 14px;
  color: #4e5969;
}
.protocol-tip.warn {
  color: #ff7d00;
}
.pick-row {
  margin-bottom: 16px;
}
.filter-row {
  margin-bottom: 16px;
}
.search-form >>> .el-form-item {
  margin-bottom: 0;
}
.param-table >>> .param-table-header {
  background: #4e5969 !important;
  color: #fff;
  font-weight: 500;
}
.param-table >>> th.param-table-header .cell {
  color: #fff;
}
.op-edit {
  color: #00b42a;
  font-size: 16px;
}
.op-del {
  color: #f53f3f;
  font-size: 16px;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.form-tip {
  font-size: 12px;
  color: #86909c;
  line-height: 1.5;
}
</style>
